// Figma: no frame provided yet — built using the established design system (bordered
// section shell + MUI Accordion, already part of this app's @mui/material dependency, so
// no new dependency needed) per the "extrapolate" decision.
// LOGIC PORTED from LC2's Invoice.js/CurrentInvoices.js/PastInvoices.js (commons/
// components/Profile/Invoice/): same `getCurrentInvoicesList`/`getPastInvoicesList`
// lazy fetches (community-scoped when a community is selected), same
// non-admin/non-Administrator filtering of the returned invoice list against
// `dashboardCommunities`, same blob-download flow (GET .../invoice/download with the
// session token header, Content-Disposition-derived filename — now shared with the
// dedicated Invoices page via commons/utils/invoiceDownload.js). Consolidated into one file
// (LC2 split Current/Past into two near-identical files sharing a duplicated
// `CommunityInvoices` component) since both this app's Invoices are the same list/download
// shape, and this pass only needs the read + download path — the two calls into one
// shared `InvoiceGroup` component below.
import React, {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {useLazyGetCurrentInvoicesListQuery, useLazyGetPastInvoicesListQuery} from '../../../features/coreApi/apiSlice';
import {getDashboardAccount, getDashboardCommunities, getDashboardCommunity} from '../../../app/store/reducers/dashboard';
import {getIsStakeAdmin, getTeamMemberRole} from '../../../app/store/reducers/session';
import {downloadInvoiceFile} from '../../../commons/utils/invoiceDownload';
import {tokens} from '../../../themes/ThemeCustomization';

const InvoiceRow = ({communityName, path}) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{py: 1}}>
        <Typography variant="body2">{communityName}</Typography>
        <Button size="small" onClick={() => downloadInvoiceFile(path).catch((error) => console.error('Error downloading invoice file:', error))}>
            Download
        </Button>
    </Stack>
);

const InvoiceList = ({data}) => {
    const userRole = useSelector(getTeamMemberRole);
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const dashboardCommunities = useSelector(getDashboardCommunities);

    const filtered = useMemo(() => {
        if (userRole === 'Administrator' || isStakeAdmin) return data;
        const communityNames = dashboardCommunities.map((c) => c.name);
        return data.filter((invoice) => communityNames.includes(invoice.community_name));
    }, [data, userRole, isStakeAdmin, dashboardCommunities]);

    if (filtered.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{py: 2}}>
                No invoices found.
            </Typography>
        );
    }

    return (
        <Stack divider={<Box sx={{borderBottom: `1px solid ${tokens.color.neutral200}`}} />}>
            {filtered.map((invoice, idx) => (
                <InvoiceRow key={`invoice-${idx}`} communityName={invoice.community_name} path={invoice.path} />
            ))}
        </Stack>
    );
};

const CurrentInvoicesSection = () => {
    const dashboardAccount = useSelector(getDashboardAccount);
    const dashboardCommunity = useSelector(getDashboardCommunity);
    const [fetchCurrentInvoices, {data: currentInvoices = {}, isLoading, isFetching, isSuccess}] = useLazyGetCurrentInvoicesListQuery();

    useEffect(() => {
        if (dashboardAccount?.sfid) {
            fetchCurrentInvoices({
                sfid: dashboardAccount.sfid,
                community_sfid: dashboardCommunity.sfid !== 'all' ? dashboardCommunity.sfid : null
            });
        }
    }, [dashboardAccount, dashboardCommunity, fetchCurrentInvoices]);

    return (
        <Accordion sx={{borderTop: `1px solid ${tokens.color.neutral200}`, boxShadow: 'none', '&:before': {display: 'none'}}}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>
                    Current invoices
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {isLoading || isFetching ? (
                    <Typography variant="body2" color="text.secondary">
                        Loading…
                    </Typography>
                ) : isSuccess && Object.keys(currentInvoices).length > 0 ? (
                    <InvoiceList data={currentInvoices} />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Current invoices are not found
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

const PastInvoicesSection = () => {
    const dashboardAccount = useSelector(getDashboardAccount);
    const dashboardCommunity = useSelector(getDashboardCommunity);
    const [fetchPastInvoices, {data: pastInvoices = [], isLoading, isFetching, isSuccess}] = useLazyGetPastInvoicesListQuery();

    useEffect(() => {
        if (dashboardAccount?.sfid) {
            fetchPastInvoices({
                sfid: dashboardAccount.sfid,
                community_sfid: dashboardCommunity.sfid !== 'all' ? dashboardCommunity.sfid : null
            });
        }
    }, [dashboardAccount, dashboardCommunity, fetchPastInvoices]);

    return (
        <Accordion sx={{borderTop: `1px solid ${tokens.color.neutral200}`, boxShadow: 'none', '&:before': {display: 'none'}}}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>
                    Past invoices
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {isLoading || isFetching ? (
                    <Typography variant="body2" color="text.secondary">
                        Loading…
                    </Typography>
                ) : isSuccess && pastInvoices.length > 0 ? (
                    pastInvoices.map((yearInvoices, yearIdx) => (
                        <Accordion key={yearIdx} sx={{boxShadow: 'none', '&:before': {display: 'none'}}}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2" fontWeight={600}>
                                    {yearInvoices.year}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{pl: 3}}>
                                {yearInvoices.months.map((monthInvoices, monthIdx) => (
                                    <Accordion key={monthIdx} sx={{boxShadow: 'none', '&:before': {display: 'none'}}}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="body2">{monthInvoices.month}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <InvoiceList data={monthInvoices.files} />
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Past invoices are not found
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

const Invoice = () => (
    <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', backgroundColor: tokens.color.white, overflow: 'hidden'}}>
        <Typography variant="subtitle1" fontWeight={600} sx={{p: 2}}>
            Invoices
        </Typography>
        <CurrentInvoicesSection />
        <PastInvoicesSection />
    </Box>
);

export default Invoice;
